import {useAccount} from "@mytour/platform";
import type {CguPolicy, IAccount} from "@mytour/shared";
import {onlineManager} from "@tanstack/react-query";
import {type ReactNode, useState, useSyncExternalStore} from "react";
import {Alert, Button, Modal, ModalBody} from "reactstrap";
import {AcceptCGUModal} from "./AcceptCGUModal.js";
import {getCguModalMode} from "./getCguModalMode.js";

export function CguGate({account, children}: {account: IAccount; children: ReactNode}) {
  const {refetchSession, isSessionFetching, sessionError} = useAccount();
  const online = useSyncExternalStore(
    (notify) => onlineManager.subscribe(notify),
    () => onlineManager.isOnline()
  );
  const [policy, setPolicy] = useState<CguPolicy | undefined>(account.cguPolicy);
  const [pending, setPending] = useState(false);
  const [submittedAccount, setSubmittedAccount] = useState<IAccount>();
  const [changed, setChanged] = useState(false);
  const [rejectedVersions, setRejectedVersions] = useState<number[]>([]);

  if (!pending && account.cguPolicy && account.cguPolicy.version !== policy?.version) {
    setPolicy(account.cguPolicy);
    setChanged(policy !== undefined);
  }
  const mode = getCguModalMode(account.cgu, account.moderation, policy?.version ?? 0);
  if (!pending && !mode) {
    return children;
  }
  if (
    !policy ||
    (!pending && (sessionError || !account.cguPolicy || rejectedVersions.includes(policy.version)))
  ) {
    return (
      <Modal isOpen backdrop="static" centered>
        <ModalBody>
          <Alert color="warning">
            Les conditions ne sont pas disponibles. Merci de réessayer.
          </Alert>
          <Button color="primary" disabled={!online || isSessionFetching} onClick={handleRetry}>
            Réessayer
          </Button>
        </ModalBody>
      </Modal>
    );
  }
  const presentedAccount = submittedAccount ?? account;
  return (
    <AcceptCGUModal
      key={policy.version}
      account={presentedAccount}
      policy={policy}
      cguUpdated={presentedAccount.cgu != null}
      changed={changed}
      disabled={!online}
      onPendingChange={handlePendingChange}
      onVersionRejected={handleVersionRejected}
    />
  );

  function handlePendingChange(pending: boolean) {
    setPending(pending);
    setSubmittedAccount(pending ? account : undefined);
  }

  function handleRetry() {
    void refetchSession();
  }

  function handleVersionRejected(version: number) {
    setRejectedVersions((versions) => [...versions, version]);
    void refetchSession();
  }
}
